import { useState } from 'react';
import {
  Box, Typography, Chip, Button, Stack, Divider,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import intl from 'components/i18n/ReactIntlWrapper';
import TrainingCourseDrawer from './TrainingCourseDrawer';
import ResizableDrawer from 'components/Layout/ResizableDrawer';

interface Props {
  open: boolean;
  onClose: () => void;
  item: any;
  skills: any[];
  onUpdate: (data: any) => Promise<void>;
  onAddSkill: (idSkill: number) => Promise<void>;
  onRemoveSkill: (skillId: number) => Promise<void>;
}

const TrainingCourseDetail: React.FC<Props> = ({
  open, onClose, item, skills, onUpdate, onAddSkill, onRemoveSkill,
}) => {
  const [editOpen, setEditOpen] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<number | ''>('');
  const [saving, setSaving] = useState(false);

  const linkedSkillIds = new Set((item.trainingCourseSkills ?? []).map((s: any) => s.idSkill ?? s.skill?.id));
  const availableSkills = skills.filter((s) => !linkedSkillIds.has(s.id));

  const handleAddSkill = async () => {
    if (!selectedSkillId) return;
    setSaving(true);
    try {
      await onAddSkill(Number(selectedSkillId));
      setSelectedSkillId('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ResizableDrawer open={open} onClose={onClose} defaultWidth={480}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">{item.name}</Typography>
          <Button size="small" onClick={() => setEditOpen(true)}>{intl.get('common.edit')}</Button>
        </Box>

        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{item.description}</Typography>
        )}
        <Stack direction="row" spacing={3} sx={{ mb: 2 }}>
          {item.duration != null && (
            <Typography variant="body2">{intl.get('hr.course.duration')}: {item.duration}h</Typography>
          )}
          {item.price != null && (
            <Typography variant="body2">{intl.get('hr.course.price')}: ${item.price}</Typography>
          )}
        </Stack>
        {item.url && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            <a href={item.url} target="_blank" rel="noreferrer">{item.url}</a>
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>{intl.get('hr.course.skills')}</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {(item.trainingCourseSkills ?? []).map((s: any) => (
            <Chip
              key={s.id}
              label={s.skill?.name}
              size="small"
              onDelete={() => onRemoveSkill(s.id)}
              deleteIcon={<DeleteIcon />}
            />
          ))}
          {(item.trainingCourseSkills ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">{intl.get('hr.course.noSkills')}</Typography>
          )}
        </Box>

        {availableSkills.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>{intl.get('hr.skill.add')}</InputLabel>
              <Select
                value={selectedSkillId}
                onChange={(e) => setSelectedSkillId(e.target.value as number)}
                label={intl.get('hr.skill.add')}
              >
                {availableSkills.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={handleAddSkill} disabled={!selectedSkillId || saving}>
              {intl.get('common.add')}
            </Button>
          </Stack>
        )}
      </ResizableDrawer>

      <TrainingCourseDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={async (data) => { await onUpdate(data); setEditOpen(false); }}
        item={item}
      />
    </>
  );
};

export default TrainingCourseDetail;
